/**
 * Biblioteca de documentos — grid con las 13 tarjetas del §4 del
 * brief. Filtro por texto (título o descripción) y un contador de
 * pendientes por subir arriba, para que se vea de un vistazo qué
 * falta.
 */

import { useMemo, useState } from "react";
import { Empty, Input, Tag, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { PageHeader } from "@/components/common/PageHeader";
import { usePageTitle } from "@/hooks";
import { documents } from "@/data/onboarding";
import { DocumentCard } from "./DocumentCard";

const { Text } = Typography;

export const DocumentsPage: React.FC = () => {
  usePageTitle("Documentos — Onboarding");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return documents;
    return documents.filter(
      (d) =>
        d.title.toLowerCase().includes(q) ||
        (d.description ?? "").toLowerCase().includes(q),
    );
  }, [query]);

  const pending = documents.filter((d) => !d.url).length;

  return (
    <>
      <PageHeader
        title="Documentos"
        subtitle="Biblioteca descargable — protocolos, contratos, presupuestos y hojas de seguimiento."
      />

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Buscar documento…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ maxWidth: 360 }}
        />
        <Text type="secondary">
          {filtered.length} de {documents.length}{" "}
          {pending > 0 ? (
            <Tag color="default" style={{ marginLeft: 6 }}>
              {pending} pendiente{pending === 1 ? "" : "s"} de subir
            </Tag>
          ) : null}
        </Text>
      </div>

      {filtered.length === 0 ? (
        <Empty description="No hay documentos que coincidan." />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 12,
          }}
        >
          {filtered.map((d) => (
            <DocumentCard key={d.slug} doc={d} />
          ))}
        </div>
      )}
    </>
  );
};
